import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const SITE_NAME = 'Inversun'

const routeSeo = {
  home: {
    title: 'Home',
    description: 'Inversun - Premium solar pump inverters, VFD inverters, reactors, and sinewave filters. Leading solar energy solutions provider in Lebanon.',
    keywords: ['solar pump inverter', 'inversun solar pump inverter', 'VFD pump inverter', 'inversun pump inverter'],
  },
  about: {
    title: 'About Us',
    description: 'Learn about Inversun - your trusted partner for solar pump inverters, reactors, and sinewave filters in Lebanon and the Middle East.',
  },
  services: {
    title: 'Service Center',
    description: 'Inversun service center - technical support, installation guidance, and maintenance for solar pump inverters and VFD systems.',
  },
  projects: {
    title: 'Projects',
    description: 'Explore Inversun solar energy projects - solar pump inverter installations, industrial VFD systems, and more.',
  },
  shop: {
    title: 'Shop',
    keywords: ['solar pump inverter', 'VFD pump inverter', 'reactor', 'sinewave filter', 'inversun'],
  },
  products: {
    title: 'Products',
    description: 'Browse Inversun product catalog - solar pump inverters, line reactors, sinewave filters, and accessories from Inversun, INVT, Veichi, and Usfull.',
    keywords: ['solar pump inverter', 'VFD pump inverter', 'reactor', 'sinewave filter', 'inversun', 'invt', 'veichi', 'usfull'],
  },
  distributors: {
    title: 'Distributors',
    description: 'Find Inversun authorized distributors for solar pump inverters, reactors, and sinewave filters.',
  },
  support: {
    title: 'Support',
    description: 'Inversun support center - product manuals, datasheets, technical documentation for solar pump inverters, reactors, and sinewave filters.',
    keywords: ['inversun support', 'solar pump inverter manual', 'VFD inverter datasheet'],
  },
  contact: {
    title: 'Contact',
    description: 'Contact Inversun for solar pump inverter inquiries, VFD system quotes, and technical support in Lebanon.',
  },
}

const routes = [
  {
    path: '/',
    name: 'home',
    component: () => import('@/views/HomeView.vue'),
    meta: { title: 'Home' }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import('@/views/PageView.vue'),
    meta: { title: 'About Us' }
  },
  {
    path: '/services',
    name: 'services',
    component: () => import('@/views/PageView.vue'),
    meta: {
      title: 'Service Center',
      cmsSlug: 'services'
    }
  },
  {
    path: '/projects',
    name: 'projects',
    component: () => import('@/views/ProjectsView.vue'),
    meta: { title: 'Projects' }
  },
  {
    path: '/projects/:slug',
    name: 'project-detail',
    component: () => import('@/views/ProjectDetailView.vue'),
    meta: { title: 'Project Details' }
  },
  {
    path: '/shop',
    name: 'shop',
    component: () => import('@/views/ShopView.vue'),
    meta: { title: 'Shop' }
  },
  {
    path: '/shop/category/:slug',
    name: 'shop-category',
    component: () => import('@/views/ShopView.vue'),
    meta: { title: 'Shop' }
  },
  {
    path: '/products',
    name: 'products',
    component: () => import('@/views/ProductsView.vue'),
    meta: { title: 'Products' }
  },
  {
    path: '/products/category/:slug',
    name: 'products-category',
    component: () => import('@/views/CategoryProductsView.vue'),
    meta: { title: 'Products' }
  },
  {
    path: '/product/:slug',
    name: 'product',
    component: () => import('@/views/ProductView.vue'),
    meta: { title: 'Product' }
  },
  {
    path: '/cart',
    name: 'cart',
    component: () => import('@/views/CartView.vue'),
    meta: { title: 'Cart' }
  },
  {
    path: '/checkout',
    name: 'checkout',
    component: () => import('@/views/CheckoutView.vue'),
    meta: { title: 'Checkout', requiresAuth: true }
  },
  // Authentication
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/LoginView.vue'),
    meta: { title: 'Login', guestOnly: true }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/RegisterView.vue'),
    meta: { title: 'Register', guestOnly: true }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: { title: 'Forgot Password', guestOnly: true }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: { title: 'Reset Password', guestOnly: true }
  },
  // Customer Account (requires auth)
  {
    path: '/account',
    name: 'account',
    component: () => import('@/views/AccountView.vue'),
    meta: { title: 'My Account', requiresAuth: true }
  },
  {
    path: '/account/orders',
    name: 'orders',
    component: () => import('@/views/OrdersView.vue'),
    meta: { title: 'My Orders', requiresAuth: true }
  },
  {
    path: '/account/orders/:id',
    name: 'order-detail',
    component: () => import('@/views/OrderDetailView.vue'),
    meta: { title: 'Order Details', requiresAuth: true }
  },
  {
    path: '/account/wishlist',
    name: 'wishlist',
    component: () => import('@/views/WishlistView.vue'),
    meta: { title: 'My Wishlist' }
  },
  {
    path: '/account/addresses',
    name: 'addresses',
    component: () => import('@/views/AddressesView.vue'),
    meta: { title: 'My Addresses', requiresAuth: true }
  },
  {
    path: '/distributors',
    name: 'distributors',
    component: () => import('@/views/PageView.vue'),
    meta: {
      title: 'Distributors',
      cmsSlug: 'distributors'
    }
  },
  {
    path: '/support',
    name: 'support',
    component: () => import('@/views/PageView.vue'),
    meta: {
      title: 'Support', cmsSlug: 'support'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import('@/views/PageView.vue'),
    meta: {
      title: 'Contact',
      cmsSlug: 'contact'
    }
  },
  // Dynamic CMS pages (for any additional pages)
  {
    path: '/pages/:slug',
    name: 'page',
    component: () => import('@/views/PageView.vue'),
    meta: { title: 'Page' }
  },
  // Dynamic single-segment CMS pages (e.g. /privacy-policy)
  {
    path: '/:slug',
    name: 'page-root',
    component: () => import('@/views/PageView.vue'),
    meta: { title: 'Page' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { title: '404' }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0, behavior: 'smooth' }
  }
})

// Navigation guards
router.beforeEach((to, from, next) => {
  // Apply SEO data from route config
  const seo = routeSeo[to.name]
  if (seo) {
    const title = `${seo.title} | ${SITE_NAME}`
    document.title = title

    // Update meta description
    if (seo.description) {
      let metaDesc = document.querySelector('meta[name="description"]')
      if (metaDesc) metaDesc.setAttribute('content', seo.description)
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]')
    if (canonical) canonical.setAttribute('href', `${window.location.origin}${to.fullPath}`)
  } else {
    document.title = to.meta.title
      ? `${to.meta.title} | ${SITE_NAME}`
      : SITE_NAME
  }
  
  // Check auth requirements
  const authStore = useAuthStore()
  authStore.init()
  
  // Route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login', query: { redirect: to.fullPath } })
    return
  }
  
  // Route is for guests only (login, register, etc.)
  if (to.meta.guestOnly && authStore.isAuthenticated) {
    next({ name: 'account' })
    return
  }
  
  next()
})

export default router
